/**
 * 대사 검토용 시각 토큰 — IXD / DSD 렌더러가 공유한다.
 *
 * 이 디렉터리는 셀에 입히는 시각을 세 개념으로 구분한다. 이름이 섞이면 바로 헷갈리므로
 * 새 시각을 추가할 때 어느 개념인지 먼저 정한다:
 * - **counterpart** — 반대편에서 선택한 셀에 *대응하는* 이쪽 셀. 선택마다 바뀐다.
 *   외곽선(+나머지 dim)으로 표시하며 이 파일이 소유한다.
 * - **region** — 셀이 속한 DL·기간 영역. 시각 토큰이 아니라 셀의 `data-dl-view-id` /
 *   `data-dl-index` / `data-period` 속성으로 표현하므로 이 파일이 아니라 CSS selector
 *   (`global.css` 의 `body[data-region-review]`)가 소유한다. 여기엔 그 CSS 와 렌더러가
 *   함께 맞춰야 하는 숫자 하나(`DL_REVIEW_COLOR_COUNT`)만 둔다.
 * - **focus** — 지금 커서가 놓인 단일 셀(`focusedDsdId` → `showRing`).
 *   counterpart 와 **같은 자리·같은 색**으로 그린다(아래 `REVIEW_FOCUS_CLASS` 주석 참고).
 * - **status** — 데이터 판정 결과라 상시 유지된다(`matchResult`, `diffStatus`,
 *   `REVIEW_NO_MATCH_CSS`). 면색으로 표시한다.
 *
 * 두 렌더러는 counterpart 를 입히는 메커니즘이 다르다:
 * - DSD (`dsd-preview-renderer.tsx`): React className (Tailwind 정적 클래스).
 *   매핑 정보가 이미 렌더 트리에 있어 선언적 적용이 자연스럽다.
 * - IXD (`use-ixd-cell-review-styles.ts`): document.head 에 `<style>` 주입.
 *   IXD chunk 트리는 무겁게 memo 되어 있어(`areChunkPropsEqual` 등) 선택할 때마다
 *   재렌더하지 않으려고 selector 기반으로 입힌다.
 *
 * Tailwind 는 빌드타임 정적 클래스만 스캔하므로 className 과 CSS 문자열 표현을
 * 런타임에 서로 derive 할 수 없다. 그래서 "같은 시각"을 두 형태로 이 파일에 함께 둔다.
 * 색·두께·opacity 를 바꿀 때는 아래 짝(class ↔ css)을 반드시 같이 수정할 것.
 */

/** counterpart 외곽선 색 — Figma primary-base 시맨틱 토큰 */
const REVIEW_COUNTERPART_COLOR = "var(--primary-base)";

// ── DSD: React className (Tailwind 정적 클래스) ──

/**
 * counterpart 셀 외곽선. status 면색 위에 얹혀 두 정보를 동시 인식하게 한다.
 * outline 이라 셀 간격을 흔들지 않는다.
 *
 * offset 은 **-1px** — 링이 셀 경계선 중앙에 걸친다(안쪽 1px + 바깥쪽 1px).
 * -2px 면 링이 셀 안쪽에 통째로 그려져, counterpart 셀이 붙어 있을 때
 * `A의 2px + 표 보더 + B의 2px` 가 나란히 쌓여 한 줄이 두 배로 두꺼워 보인다.
 * 중앙에 걸치면 인접한 두 링이 같은 자리에 겹쳐 하나의 2px 밴드가 된다.
 * → IXD 의 `REVIEW_COUNTERPART_CSS` 와 동일한 시각.
 */
export const REVIEW_COUNTERPART_CLASS =
  "outline outline-2 outline-primary-base outline-offset-[-1px] z-10";

/**
 * focus 셀 외곽선 — **counterpart 와 같은 자리(offset -1px)에 그린다.**
 *
 * 예전엔 `ring-2 ring-inset`(셀 안쪽 2px)이었는데, 그러면 셀 경계에 그려지는 링
 * (counterpart 외곽선·다중선택 검은 테두리)과 자리가 달라 **나란히 쌓인다** — 그 셀만
 * 테두리가 두 배로 두꺼워 보였다. 겹치는 경로가 둘 있었다:
 * - focus + counterpart: IXD 선택이 매칭을 찾으면 focus 를 안 거둔다
 *   (`compare-content-view` 는 매칭이 하나도 없을 때만 `clearFocus()`).
 * - focus + 다중선택: 드래그해도 focus 는 남는다(방향키 커서라 거두면 안 된다).
 *
 * 같은 자리로 옮기면 링이 겹쳐 두께가 2px 로 고정된다. 한 요소는 outline 을 하나만
 * 가지므로 다중선택된 셀에서는 주입 CSS 의 `outline:...!important` 가 이겨 검은 테두리만
 * 남는데, 이는 "2개 이상이면 검은 테두리"라는 원래 설계 의도와 같다.
 *
 * counterpart 와 색까지 같아 시각적으로 구분되지 않는다 — 의도한 것이다(원래도 색은 같았다).
 * offset 을 바꿀 땐 `REVIEW_COUNTERPART_CLASS` 와 **반드시 같이** 바꿔야 다시 안 쌓인다.
 */
export const REVIEW_FOCUS_CLASS =
  "outline outline-2 outline-primary-base outline-offset-[-1px] z-10";

/** counterpart 가 아닌 셀 dim (DSD 전용 — IXD 는 작업 surface 라 dim 하지 않는다) */
export const REVIEW_DIM_CLASS = "opacity-40";

// ── IXD: <style> 주입용 CSS 선언 문자열 ──

/**
 * counterpart 셀 외곽선. → DSD 의 `REVIEW_COUNTERPART_CLASS` 와 동일한 시각.
 * offset -1px 로 셀 경계선 중앙에 걸친다(위 클래스 주석의 이유와 동일).
 */
export const REVIEW_COUNTERPART_CSS = `outline:2px solid ${REVIEW_COUNTERPART_COLOR}!important;outline-offset:-1px;position:relative;z-index:10`;

/** 매칭 DSD 가 없는 IXD 값 셀 표시 색 — Figma status-negative 시맨틱 토큰 */
const NO_MATCH_COLOR = "var(--status-negative)";

/**
 * 매칭되는 DSD 셀이 없는 IXD 값 셀을 상시 옅은 빨강 배경으로 표시(IXD 전용).
 * 외곽선 없이 면색만으로 알린다.
 */
export const REVIEW_NO_MATCH_CSS = `background-color:color-mix(in srgb, ${NO_MATCH_COLOR} 10%, transparent)!important`;

/**
 * 배정 검토 모드에서 DL 영역을 구분하는 색의 개수. 렌더러는 이 수로 `data-dl-index` 를
 * 순환시키고 `global.css` 는 그 수만큼 색 규칙(`--definition-cell-*-bg`, 워크북 매핑
 * 탭과 같은 색)을 둔다 — 한쪽만 바꾸면 색이 없는 영역이 생기므로 **둘을 함께 수정**한다.
 *
 * chunk 안 DL 이 이 수를 넘으면 색이 겹친다. 순번은 **등장 순서**로 매기므로 겹치는 두
 * DL 사이에는 항상 다른 색 3개가 끼어 있어 인접 영역끼리 같은 색이 되기는 어렵지만,
 * 표 배치에 따라 붙을 수 있다. 겹친 두 영역이 실제로 붙어 한 영역으로 읽히는 것이
 * 확인되면 색을 늘리거나 직전 영역과 다른 색을 고르는 배정으로 바꾼다.
 */
export const DL_REVIEW_COLOR_COUNT = 4;

/**
 * 배정 검토 모드에서 항목(line_item) 영역을 구분하는 색의 개수. `data-line-item-index`
 * 를 이 수로 순환시키고 `global.css` 가 그만큼 색 규칙(`--line-item-cell-*-bg`)을 둔다 —
 * `DL_REVIEW_COLOR_COUNT` 와 같은 규약이라 **둘을 함께 수정**한다.
 *
 * DL 4색(`--definition-cell-*-bg`)을 재사용하지 않는 이유는 **인접 조건이 다르기**
 * 때문이다. DL 영역은 한 표에 많아야 두셋이고 서로 떨어져 있지만, 항목은 행마다 배정돼
 * 위아래가 항상 맞닿는다. DL 4색에는 대비가 약한 쌍이 있어(연녹/연라임) 그 순번이 이웃하면
 * 경계가 안 보인다. 그래서 색상환에서 서로 떨어진 6색을 따로 둔다.
 *
 * 순번은 **등장 순서**이고 항목 단위로 고정한다(같은 항목은 표 어디서나 같은 색) — DL 과
 * 같은 규칙이다. 항목이 6개를 넘으면 색이 겹치는데, 겹치는 두 항목 사이에는 다른 색 5개가
 * 끼어 있어 이웃한 두 행이 같은 색이 되려면 그 다섯이 모두 다른 곳에 있어야 한다.
 */
export const LINE_ITEM_REVIEW_COLOR_COUNT = 6;
